import { NotificationType, TNotification } from '@/context/types/Notification';

/**
 * If there are variables, you can do so by wrapping the variable name in curly braces
 * i.e. Notification successfully created for "{name}"
 */
export const notifications: TNotification[] = [
  {
    type: NotificationType.LOST_CONNECTION,
    title: 'Xero connection lost',
    description: 'An error has occurred. Please review your Xero connection.',
    variant: 'error'
  },
  {
    type: NotificationType.REAUTHENTICATION_REQUIRED,
    title: 'Xero re-authentication required',
    description: 'A new feature requires additional permissions. Please reconnect with Xero.',
    variant: 'error'
  },
  {
    type: NotificationType.CONNECT_SUCCESS,
    title: 'Xero connected',
    description: 'Your Emerge account is now successfully connected to Xero',
    variant: 'success'
  },
  {
    type: NotificationType.DISCONNECT_SUCCESS,
    title: 'Xero disconnected',
    description: 'Your Xero connection has been successfully disconnected',
    variant: 'success'
  },
  {
    type: NotificationType.CREATE_BATCH_SUCCESS,
    title: '',
    description: 'Batch payment "{name}" has been added to schedule',
    variant: 'success_sm'
  },
  {
    type: NotificationType.BATCH_DELETED,
    title: '',
    description: 'Batch payment "{name}" has been deleted from schedule',
    variant: 'error_sm'
  },
  {
    type: NotificationType.BATCH_PAYMENT_COMPLETE,
    title: 'Batch payment complete',
    description: 'Your batch "{name}" has been successfully completed.',
    variant: 'batch_complete'
  },
  {
    type: NotificationType.BATCH_PAYMENT_INCOMPLETE,
    title: 'Batch processed but incomplete',
    description: 'Payments in your batch "{name}" have failed. Please review.',
    variant: 'batch_incomplete'
  },
  {
    type: NotificationType.TRANSACTION_GL_CODE_ADD_SUCCESS,
    title: 'GL code added',
    description: 'The GL code was successfully added to this transaction',
    variant: 'success_sm'
  },
  {
    type: NotificationType.TRANSACTION_GL_CODE_UPDATE_SUCCESS,
    title: 'GL code updated',
    description: 'The GL code was successfully updated on this transaction',
    variant: 'success_sm'
  },
  {
    type: NotificationType.TRANSACTION_GL_CODE_REMOVE_SUCCESS,
    title: 'GL code removed',
    description: 'The GL code was successfully removed from this transaction',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODES_ENABLED,
    title: 'GL codes enabled',
    description: 'GL codes were successfully enabled',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODES_DISABLED,
    title: 'GL codes disabled',
    description: 'GL codes were successfully disabled',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODE_ARCHIVED,
    title: 'GL code archived',
    description: 'GL code was successfully archived',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODE_DELETED,
    title: 'GL code deleted',
    description: 'GL code was successfully deleted',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODE_RESTORED,
    title: 'GL code restored',
    description: 'GL code was successfully restored',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODE_UPDATED,
    title: 'GL code updated',
    description: 'GL code was successfully updated',
    variant: 'success_sm'
  },
  {
    type: NotificationType.GL_CODES_SAVED,
    title: 'GL codes saved',
    description: 'GL codes were successfully saved',
    variant: 'success_sm'
  },
  {
    type: NotificationType.ATTACHMENT_REMOVED,
    title: 'Attachment removed',
    description: 'Attachment was successfully removed from this transaction',
    variant: 'success_sm'
  },
  {
    type: NotificationType.ATTACHMENT_ADDED,
    title: '{title} added',
    description: '{prefix} successfully added to this transaction',
    variant: 'success_sm'
  },
  {
    type: NotificationType.ATTACHMENT_UPLOAD_ERROR,
    title: 'Attachment upload error',
    description: '{error}',
    variant: 'error_sm'
  },
  {
    type: NotificationType.NO_AVAILABLE_XERO_ORGANIZATION,
    title: 'Unsuccessful Xero connection',
    description:
      'Unable to connect your business with a Xero organization. Please reconnect with Xero.',
    variant: 'error'
  },
  {
    type: NotificationType.PASSKEY_CREATED,
    title: 'Passkey created',
    description: 'Your new passkey can now be used to sign in to your Emerge account',
    variant: 'success_sm'
  },
  {
    type: NotificationType.PASSKEY_REMOVED,
    title: 'Passkey removed',
    description: 'The passkey was successfully removed from your account',
    variant: 'success_sm'
  },
  {
    type: NotificationType.TEAM_MEMBER_CARDS_FROZEN,
    title: 'Cards frozen',
    description: 'Your team’s cards are frozen and can’t be used for purchases.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_CARDS_UNFROZEN,
    title: 'Cards unfrozen',
    description: 'Your team’s cards are unfrozen and can be used for purchases.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBERS_PAUSED,
    title: 'Team members paused',
    description: 'Your team members have been paused and will not have access.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBERS_ENABLED,
    title: 'Team members enabled',
    description: 'Your team members have been enabled and can now access their cards.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBERS_REMOVED,
    title: 'Team members removed',
    description: '{names} {isAre} no longer part of this account.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_ACCESS_LEVEL_UPDATED,
    title: 'Account access updated',
    description: 'Their access has been updated to {accessLevel}.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_CARD_CREATED,
    title: 'Card created',
    description: 'A new card has been created for {name}.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_INVITE_UPDATED,
    title: 'Invitation updated',
    description: '{name} will get an updated email to join this account.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_INVITE_DELETED,
    title: 'Invite deleted',
    description: '{name} invite deleted',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_CARD_DELETED,
    title: 'Card deleted',
    description: 'The card has been deleted.',
    variant: 'success'
  },
  {
    type: NotificationType.TEAM_MEMBER_CARD_SETTINGS_UPDATED,
    title: 'Card settings updated',
    description: 'The card’s limits and permissions have been updated.',
    variant: 'success'
  },
  {
    type: NotificationType.UNKNOWN_ERROR,
    title: 'Something went wrong',
    description: 'An error has occurred. Please contact support if still persists',
    variant: 'error'
  }
];
